import { useState } from "react";
import {
  MapPin,
  Phone,
  ExternalLink,
  Globe,
  Camera,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ClassificationsSection } from "@/components/map/ClassificationsSection";
import { PhotoCarousel } from "@/components/map/PhotoCarousel";
import { getCourtColor } from "@/components/map/SportCourtMarker";
import { sportTypeIcons } from "@/lib/sport-icons";
import { resolveHasSportField } from "@/lib/classification";
import { StatusIndicator } from "@/components/locations/StatusIndicator";
import { trpc } from "@/lib/trpc";

interface PublicLocationSidebarDetailProps {
  token: string;
  locationId: string;
  onScreenshotClick: () => void;
}

export function PublicLocationSidebarDetail({
  token,
  locationId,
  onScreenshotClick,
}: PublicLocationSidebarDetailProps) {
  const { data: location, isLoading } = trpc.share.getLocation.useQuery(
    { token, locationId },
    { enabled: !!locationId },
  );

  const { data: screenshotData } = trpc.share.getScreenshotUrl.useQuery(
    { token, locationId },
    { enabled: !!locationId && !!location?.screenshot },
  );

  const { data: spots } = trpc.share.getSportCourts.useQuery(
    { token, locationId },
    { enabled: !!locationId },
  );

  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-48 w-full rounded-lg" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (!location) return null;

  const screenshotUrl = screenshotData?.url ?? null;
  const satelliteClassification = location.classifications?.find(
    (c: { source: string }) => c.source === "satellite",
  );
  const webResearchClassification = location.classifications?.find(
    (c: { source: string }) => c.source === "web_research",
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto space-y-4 p-4">
        {/* Screenshot */}
        <button
          type="button"
          className="relative group h-48 w-full rounded-lg overflow-hidden border bg-muted cursor-pointer"
          onClick={onScreenshotClick}
          title="Zoom to location on map"
        >
          {screenshotUrl && !imgError ? (
            <>
              {imgLoading && (
                <Skeleton className="absolute inset-0 h-full w-full rounded-lg" />
              )}
              <img
                src={screenshotUrl}
                alt={`Satellite view of ${location.name}`}
                className={`h-full w-full object-cover transition-opacity ${imgLoading ? "opacity-0" : "opacity-100"}`}
                onLoad={() => setImgLoading(false)}
                onError={() => {
                  setImgLoading(false);
                  setImgError(true);
                }}
              />
            </>
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <Camera className="size-8 text-muted-foreground" />
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
            <MapPin className="size-5 text-white" />
          </div>
        </button>

        {/* Place photos */}
        {location.photo_refs?.length > 0 && (
          <PhotoCarousel photoRefs={location.photo_refs} />
        )}

        {/* Address */}
        {location.address && (
          <div>
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
              Address
            </h3>
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="size-4 text-muted-foreground mt-0.5 shrink-0" />
              <span>{location.address}</span>
            </div>
          </div>
        )}

        {/* Status indicators */}
        <div className="flex gap-2">
          <StatusIndicator
            type="sport_field"
            hasSportField={resolveHasSportField(location.classifications)}
          />
          <StatusIndicator
            type="ownership"
            ownership={location.ownership}
          />
          <StatusIndicator
            type="satellite_confidence"
            confidence={satelliteClassification?.confidence ?? null}
          />
          <StatusIndicator
            type="web_confidence"
            confidence={webResearchClassification?.confidence ?? null}
          />
          <StatusIndicator
            type="reviews"
            rating={location.rating}
            reviewCount={location.user_rating_count}
          />
        </div>

        {/* Contact info */}
        <div className="space-y-2 text-sm">
          {location.phone && (
            <div className="flex items-center gap-2">
              <Phone className="size-4 text-muted-foreground shrink-0" />
              <a
                href={`tel:${location.phone}`}
                className="text-primary hover:underline"
              >
                {location.phone}
              </a>
            </div>
          )}
        </div>

        {/* Classifications */}
        <div className="rounded-lg border bg-card p-3">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Classifications
          </h3>
          <ClassificationsSection
            classifications={location.classifications ?? []}
            ownership={location.ownership}
            hasWebsite={!!location.website}
            isRejected={false}
          />
        </div>

        {/* Sport Courts (view-only) */}
        {spots && spots.length > 0 && (
          <div>
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Sport Courts ({spots.length})
            </h3>
            <div className="space-y-1.5">
              {spots.map((court: any) => {
                const Icon = sportTypeIcons[court.sport_type];
                const color = getCourtColor(court.sport_type);
                return (
                  <div
                    key={court.id}
                    className="flex items-center gap-2 rounded-md border p-2 text-sm"
                  >
                    <div
                      className="flex items-center justify-center rounded-full shrink-0"
                      style={{ width: 20, height: 20, backgroundColor: color }}
                    >
                      {Icon && <Icon size={12} stroke={1.5} color="white" />}
                    </div>
                    <span className="text-xs font-medium truncate">
                      {court.sport_type.replace(/_/g, " ")}
                    </span>
                    {court.notes && (
                      <span className="text-xs text-muted-foreground truncate flex-1">
                        {court.notes}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom action bar */}
      <div className="border-t bg-background px-4 py-3 shrink-0 flex gap-2">
        {location.google_maps_url && (
          <Button
            size="lg"
            className="flex-1 gap-1.5 bg-blue-600 text-white hover:bg-blue-700"
            asChild
          >
            <a
              href={location.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="size-4" />
              Google Maps
            </a>
          </Button>
        )}
        {location.website ? (
          <Button
            size="lg"
            className="flex-1 gap-1.5 bg-violet-600 text-white hover:bg-violet-700"
            asChild
          >
            <a
              href={location.website}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Globe className="size-4" />
              Website
            </a>
          </Button>
        ) : (
          <Button
            size="lg"
            className="flex-1 gap-1.5 bg-muted text-muted-foreground"
            disabled
          >
            <Globe className="size-4" />
            No website
          </Button>
        )}
      </div>
    </div>
  );
}
